import React from "react";
import { Building2, User } from "lucide-react";
import { nationalityToCountry } from "demonym";
import { PanelShell, type PanelMeta } from "./PanelShell";
import { natureLabelByCode } from "@/lib/psc-descriptions";

type Officer = {
  Hash_Key: string;
  Full_Name: string;
  Appointment_Type: string;
  Appointment_Date: string;
  Resignation_Date?: string | null;
  Occupation?: string | null;
  Partial_Date_Of_Birth?: string | null;
  Nationality?: string | null;
  Full_Address?: string | null;
  Address_Line_1?: string | null;
  Address_Line_2?: string | null;
  Post_Town?: string | null;
  County?: string | null;
  Country?: string | null;
  Person_Postcode?: string | null;
};

type OfficerResponse = {
  active_officers: Officer[];
  resigned_officers: Officer[];
  summary: {
    total_officers: number;
    active_directors: number;
    resigned_directors: number;
    company_secretaries: number;
    corporate_directors: number;
  };
};

type Psc = {
  Hash_Key: string;
  Data_Name: string;
  Data_Kind: string;
  Natures_Of_Control: string[];
  Data_Natures_Of_Control_0_?: string | null;
  Data_Natures_Of_Control_1_?: string | null;
  Data_Natures_Of_Control_2_?: string | null;
  Data_Natures_Of_Control_3_?: string | null;
  Data_Natures_Of_Control_4_?: string | null;
  Data_Natures_Of_Control_5_?: string | null;
  Data_Natures_Of_Control_6_?: string | null;
  Data_Natures_Of_Control_7_?: string | null;
  Data_Natures_Of_Control_8_?: string | null;
  Data_Natures_Of_Control_9_?: string | null;
  Data_Natures_Of_Control_10_?: string | null;
  Data_Natures_Of_Control_11_?: string | null;
  Data_Name_Elements_Forename?: string | null;
  Data_Name_Elements_Surname?: string | null;
  Data_Nationality?: string | null;
  Data_Country_Of_Residence?: string | null;
  Data_Date_Of_Birth_Month?: number | null;
  Data_Date_Of_Birth_Year?: number | null;
  Full_Address?: string | null;
  Data_Address_Address_Line_1?: string | null;
  Data_Address_Address_Line_2?: string | null;
  Data_Address_Locality?: string | null;
  Data_Address_Region?: string | null;
  Data_Address_Country?: string | null;
  Data_Address_Postal_Code?: string | null;
  Data_Notified_On?: string | null;
  Data_Ceased?: boolean | number | null;
  Data_Ceased_On?: string | null;
};

type PscResponse = {
  active_pscs: Psc[];
  ceased_pscs: Psc[];
  summary: {
    total_pscs: number;
    active_pscs: number;
    ceased_pscs: number;
    individual_pscs: number;
    corporate_pscs: number;
    sanctioned_pscs: number;
  };
};

type WhoPanelProps = {
  panel: PanelMeta;
  headerColor: string;
  layoutEdit: boolean;
  reportMode?: boolean;
  reportHideSummary?: boolean;
  officers: OfficerResponse | null;
  officersLoading: boolean;
  officersError: string | null;
  psc: PscResponse | null;
  pscLoading: boolean;
  pscError: string | null;
};

const formatDate = (value?: string | null) => {
  if (!value || value.startsWith("0000-00-00")) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatTenure = (value?: string | null) => {
  if (!value) return "—";
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return "—";
  const now = new Date();
  let months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) {
    months -= 1;
  }
  if (months < 0) return "—";
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  const yearLabel = years === 1 ? "yr" : "yrs";
  const monthLabel = remainingMonths === 1 ? "mo" : "mos";
  if (years > 0 && remainingMonths > 0) return `${years} ${yearLabel} ${remainingMonths} ${monthLabel}`;
  if (years > 0) return `${years} ${yearLabel}`;
  return `${remainingMonths} ${monthLabel}`;
};

const formatAddress = (officer: Officer) => {
  if (officer.Full_Address) return officer.Full_Address;
  const parts = [
    officer.Address_Line_1,
    officer.Address_Line_2,
    officer.Post_Town,
    officer.County,
    officer.Country,
    officer.Person_Postcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "—";
};

const nationalityToFlagCode = (nationality?: string | null) => {
  if (!nationality) return "";
  const match = nationalityToCountry(nationality);
  return match?.code ? match.code.toLowerCase() : "";
};

const isoToFlagEmoji = (code: string) => {
  const upper = code.trim().toUpperCase();
  if (upper.length !== 2) return "";
  const points = [...upper].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...points);
};

const twemojiUrlFromEmoji = (emoji: string) => {
  const codepoints = Array.from(emoji)
    .map((char) => char.codePointAt(0)?.toString(16))
    .filter(Boolean)
    .join("-");
  return codepoints
    ? `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${codepoints}.svg`
    : "";
};

const formatNationality = (nationality?: string | null) => {
  if (!nationality) return "—";
  const code = nationalityToFlagCode(nationality);
  const emoji = code ? isoToFlagEmoji(code) : "";
  const twemojiUrl = emoji ? twemojiUrlFromEmoji(emoji) : "";
  return (
    <span className="inline-flex items-center gap-1">
      {twemojiUrl ? (
        <img
          src={twemojiUrl}
          width={30}
          height={30}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          className="inline-block"
        />
      ) : null}
      <span>{nationality}</span>
    </span>
  );
};

const formatPscAddress = (pscItem: Psc) => {
  if (pscItem.Full_Address) return pscItem.Full_Address;
  const parts = [
    pscItem.Data_Address_Address_Line_1,
    pscItem.Data_Address_Address_Line_2,
    pscItem.Data_Address_Locality,
    pscItem.Data_Address_Region,
    pscItem.Data_Address_Country,
    pscItem.Data_Address_Postal_Code,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "—";
};

const formatPscDob = (pscItem: Psc) => {
  const month = pscItem.Data_Date_Of_Birth_Month;
  const year = pscItem.Data_Date_Of_Birth_Year;
  if (!month && !year) return "—";
  const monthString = month ? String(month).padStart(2, "0") : "—";
  return `${monthString}/${year ?? "—"}`;
};


type HeadingKey =
  | "ownership"
  | "voting"
  | "board_control"
  | "sioc"
  | "surplus_assets"
  | "nominee_status"
  | "other";

const headingLabels: Record<HeadingKey, string> = {
  ownership: "Ownership of shares",
  voting: "Voting rights",
  board_control: "Board / appointment control",
  sioc: "Significant influence or control",
  surplus_assets: "Surplus assets (LLP)",
  nominee_status: "Nominee status (ROE)",
  other: "Other",
};

const headingOrder: HeadingKey[] = [
  "ownership",
  "voting",
  "board_control",
  "sioc",
  "surplus_assets",
  "nominee_status",
  "other",
];

const extractNatureCodes = (pscItem: Psc) => {
  const rawValues = [
    pscItem.Data_Natures_Of_Control_0_,
    pscItem.Data_Natures_Of_Control_1_,
    pscItem.Data_Natures_Of_Control_2_,
    pscItem.Data_Natures_Of_Control_3_,
    pscItem.Data_Natures_Of_Control_4_,
    pscItem.Data_Natures_Of_Control_5_,
    pscItem.Data_Natures_Of_Control_6_,
    pscItem.Data_Natures_Of_Control_7_,
    pscItem.Data_Natures_Of_Control_8_,
    pscItem.Data_Natures_Of_Control_9_,
    pscItem.Data_Natures_Of_Control_10_,
    pscItem.Data_Natures_Of_Control_11_,
  ];
  const codes = rawValues
    .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
    .filter(Boolean);
  return Array.from(new Set(codes));
};

const prettifyNatureCode = (code: string) =>
  code
    .replace(/-/g, " ")
    .replace(/\bllp\b/gi, "LLP")
    .replace(/\broe\b/gi, "ROE");

const getBandRank = (code: string) => {
  if (code.includes("-75-to-100-percent")) return 3;
  if (code.includes("-50-to-75-percent")) return 2;
  if (code.includes("-25-to-50-percent")) return 1;
  if (code.includes("more-than-25-percent")) return 1;
  return 0;
};

const bucketNatureCodes = (codes: string[]) => {
  const grouped: Record<HeadingKey, { code: string; label: string; bandRank: number }[]> = {
    ownership: [],
    voting: [],
    board_control: [],
    sioc: [],
    surplus_assets: [],
    nominee_status: [],
    other: [],
  };

  codes.forEach((code) => {
    let key: HeadingKey = "other";
    if (code.startsWith("ownership-of-shares-")) key = "ownership";
    else if (code.startsWith("voting-rights-")) key = "voting";
    else if (code.startsWith("right-to-appoint-and-remove-")) key = "board_control";
    else if (code.startsWith("significant-influence-or-control")) key = "sioc";
    else if (code.startsWith("right-to-share-surplus-assets-") || code.startsWith("part-right-to-share-surplus-assets-")) {
      key = "surplus_assets";
    } else if (code.startsWith("registered-owner-as-nominee-")) {
      key = "nominee_status";
    }
    const label = natureLabelByCode[code] ?? prettifyNatureCode(code);
    if (!natureLabelByCode[code]) {
      console.warn("[psc] missing nature mapping", code);
    }
    grouped[key].push({ code, label, bandRank: getBandRank(code) });
  });

  headingOrder.forEach((key) => {
    grouped[key].sort((a, b) => {
      if (b.bandRank !== a.bandRank) return b.bandRank - a.bandRank;
      return a.label.localeCompare(b.label);
    });
  });

  const orderedKeys = headingOrder.filter((key) => grouped[key].length > 0);
  return { orderedKeys, grouped };
};

const renderControls = (codes: string[]) => {
  if (!codes.length) return null;
  const { orderedKeys, grouped } = bucketNatureCodes(codes);
  if (!orderedKeys.length) return null;
  return (
    <div className="space-y-1">
      {orderedKeys.map((key) => (
        <div key={key}>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{headingLabels[key]}</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] text-muted-foreground">
            {grouped[key].map((item) => (
              <li key={item.code}>{item.label}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const isNullDate = (d?: string | null) =>
  !d || d === "0000-00-00" || d === "0000-00-00T00:00:00.000Z";

export function WhoPanel({
  panel,
  headerColor,
  layoutEdit,
  reportMode = false,
  reportHideSummary = false,
  officers,
  officersLoading,
  officersError,
  psc,
  pscLoading,
  pscError,
}: WhoPanelProps) {
  // Officers whose Resignation_Date is "0000-00-00" are backend DB nulls — treat as active
  const resignedRaw = officers?.resigned_officers ?? [];
  const effectivelyActive = resignedRaw.filter((o) => isNullDate(o.Resignation_Date));
  const genuinelyResigned = resignedRaw.filter((o) => !isNullDate(o.Resignation_Date));

  const activeOfficers = [...(officers?.active_officers ?? []), ...effectivelyActive]
    .sort((a, b) => {
      // Longest-serving first: earliest Appointment_Date wins.
      // Officers with no/invalid date sort to the end.
      const da = a.Appointment_Date ? new Date(a.Appointment_Date).getTime() : Infinity;
      const db = b.Appointment_Date ? new Date(b.Appointment_Date).getTime() : Infinity;
      return da - db;
    });
  const activePscs = psc?.active_pscs ?? [];
  const officersToShow = reportMode ? activeOfficers : activeOfficers.slice(0, 6);
  const pscsToShow = reportMode ? activePscs : activePscs.slice(0, 6);

  const personCardClass = reportMode
    ? "rounded-md border border-transparent bg-transparent p-2 report-avoid-break"
    : "rounded-md border border-border bg-card p-2";

  return (
    <PanelShell panel={panel} headerColor={headerColor} layoutEdit={layoutEdit} reportMode={reportMode}>
      <div
        className={`${reportMode ? "mt-1 gap-1.5" : "mt-3 gap-3"} flex flex-col text-[11px] text-muted-foreground`}
      >
        {!reportHideSummary && (
          <div
            className={`${
              reportMode ? "flex flex-wrap gap-3 text-[10px]" : "grid grid-cols-2 gap-3 text-[11px]"
            } text-muted-foreground`}
          >
            <div>
              <span className="uppercase text-[10px] tracking-wide text-muted-foreground">Active officers</span>
              <div
              className={`${reportMode ? "text-xs" : "text-sm"} font-semibold text-foreground`}
              suppressHydrationWarning={reportMode}
            >
                {officersLoading ? "Loading…" : activeOfficers.length}
              </div>
            </div>
            <div>
              <span className="uppercase text-[10px] tracking-wide text-muted-foreground">Active PSCs</span>
              <div
              className={`${reportMode ? "text-xs" : "text-sm"} font-semibold text-foreground`}
              suppressHydrationWarning={reportMode}
            >
                {pscLoading ? "Loading…" : activePscs.length}
              </div>
            </div>
          </div>
        )}

        {officersError ? <div className="text-xs text-amber-600 dark:text-amber-400">{officersError}</div> : null}
        {pscError ? <div className="text-xs text-amber-600 dark:text-amber-400">{pscError}</div> : null}

        <div className={`${reportMode ? "flex flex-col gap-2.5" : "grid gap-3"}`}>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Officers</div>
            {activeOfficers.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">
                {officersLoading ? "Loading officers…" : "No active officers available."}
              </div>
            ) : (
              <div className={reportMode ? "space-y-1" : "space-y-2"}>
                {officersToShow.map((officer) => (
                  <div key={officer.Hash_Key} className={personCardClass}>
                    <div className="flex items-center gap-2 text-foreground">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-foreground">
                          {officer.Full_Name || "—"}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {officer.Appointment_Type || "Officer"}
                        </div>
                      </div>
                    </div>
                    <div className={`${reportMode ? "mt-1" : "mt-2"} grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground`}>
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">DOB</span>
                        <div>{officer.Partial_Date_Of_Birth || "—"}</div>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Nationality</span>
                        <div>{formatNationality(officer.Nationality)}</div>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Occupation</span>
                        <div className="truncate">{officer.Occupation || "—"}</div>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Address</span>
                        <div className="truncate">{formatAddress(officer)}</div>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Appointed</span>
                        <div>{formatDate(officer.Appointment_Date)}</div>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Resigned</span>
                        <div>{formatDate(officer.Resignation_Date ?? null)}</div>
                      </div>
                    </div>
                    <div className={`${reportMode ? "mt-1" : "mt-2"} text-[10px] uppercase tracking-wide text-muted-foreground`}>
                      Tenure: <span className="text-[11px] normal-case text-muted-foreground">{formatTenure(officer.Appointment_Date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!reportMode && genuinelyResigned.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Former officers</div>
              <div className="space-y-2">
                {genuinelyResigned.slice(0, 4).map((officer) => (
                  <div key={officer.Hash_Key} className={personCardClass}>
                    <div className="flex items-center gap-2 text-foreground">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted opacity-60">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-xs font-semibold text-foreground opacity-70">
                          {officer.Full_Name || "—"}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {officer.Appointment_Type || "Officer"} - Resigned
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Appointed</span>
                        <div>{formatDate(officer.Appointment_Date)}</div>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Resigned</span>
                        <div>{formatDate(officer.Resignation_Date ?? null)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Persons with significant control</div>
            {activePscs.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">
                {pscLoading ? "Loading PSCs…" : "No active PSCs available."}
              </div>
            ) : (
              <div className={reportMode ? "space-y-1" : "space-y-2"}>
                {pscsToShow.map((item) => {
                  const isCorporate = (item.Data_Kind || "").toLowerCase().includes("corporate");
                  const Icon = isCorporate ? Building2 : User;
                  return (
                    <div key={item.Hash_Key} className={personCardClass}>
                      <div className="flex items-center gap-2 text-foreground">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-xs font-semibold text-foreground">
                            {item.Data_Name || "—"}
                          </div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            {item.Data_Kind || "PSC"}
                          </div>
                        </div>
                      </div>
                      <div className={`${reportMode ? "mt-1" : "mt-2"} grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground`}>
                        <div>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">DOB</span>
                          <div>{formatPscDob(item)}</div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Nationality</span>
                          <div>{formatNationality(item.Data_Nationality)}</div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Residence</span>
                          <div className="truncate">{item.Data_Country_Of_Residence || "—"}</div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Address</span>
                          <div className="truncate">{formatPscAddress(item)}</div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Notified</span>
                          <div>{formatDate(item.Data_Notified_On ?? null)}</div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Ceased</span>
                          <div>{formatDate(item.Data_Ceased_On ?? null)}</div>
                        </div>
                      </div>
                      <div className={`${reportMode ? "mt-1" : "mt-2"}`}>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Controls</span>
                        <div className="mt-1">{renderControls(extractNatureCodes(item)) ?? "—"}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {!reportMode && process.env.NEXT_PUBLIC_MAINTENANCE_MODE === "true" && (
          <div className="space-y-2">
            <details className="rounded-md border border-border bg-card">
              <summary className="cursor-pointer select-none px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                Officers payload
              </summary>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words px-2 pb-2 text-[11px] text-muted-foreground">
{JSON.stringify(officers ?? null, null, 2)}
              </pre>
            </details>
            <details className="rounded-md border border-border bg-card">
              <summary className="cursor-pointer select-none px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                PSC payload
              </summary>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words px-2 pb-2 text-[11px] text-muted-foreground">
{JSON.stringify(psc ?? null, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </PanelShell>
  );
}
